import {
  createRef,
  type SelectHTMLAttributes,
  useEffect,
  type MouseEvent,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import styled, { css, type DefaultTheme, type StyledComponent } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import type { FieldHelperProps } from 'formik';

import { CheckIcon } from '@yoweb/ui/components/Icon/CheckIcon';
import { formStylesCss } from '@yoweb/ui/components/form/formStyles';
import { isDefined } from '@yoweb/utils/isDefined';
import { isNumber } from '@yoweb/utils/isNumber';
import {
  KEY_ARROW_DOWN,
  KEY_ARROW_UP,
  KEY_ENTER,
  KEY_SPACE,
  KEY_ESCAPE,
  KEY_TAB,
} from '@yoweb/ui/constants/keyboardKey';
import { CustomOptions, StyledOption } from './CustomOptions';

export const SELECT_KEYBOARD_KEYS = [
  KEY_SPACE,
  KEY_ENTER,
  KEY_ARROW_DOWN,
  KEY_ARROW_UP,
  KEY_ESCAPE,
];

export type SelectOptions = {
  value: string;
  label: string;
};

type DefaultSelectDropdownProps = {
  options: SelectOptions[];
  defaultValue?: string | number | readonly string[];
  label?: string;
  error?: string;
  id: string;
  withBorder?: boolean;
  onClick?(value: unknown): void;
  placeholder?: string;
  maxHeightOptions?: number;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'required'> &
  Pick<FieldHelperProps<unknown>, 'setValue'>;

export interface WrapperSelectDropdownProps {
  hasError: boolean;
  hasLabel: boolean;
  hasValue: boolean;
  hasFocus?: boolean;
  hasDelete?: boolean;
  hasPlaceholder: boolean;
  disabled?: boolean;
  $deviceNoHover?: boolean;
}

export type SelectDropdownProps = {
  wrapperComponent: StyledComponent<'select', DefaultTheme, WrapperSelectDropdownProps, never>;
} & DefaultSelectDropdownProps &
  DataTestId;

const SelectDropdown = ({
  id,
  placeholder,
  label,
  value,
  defaultValue,
  error,
  onClick,
  onFocus,
  onBlur,
  options = [],
  setValue,
  // Default height on custom select options container
  maxHeightOptions = 500,
  wrapperComponent: WrapperComponent,
  disabled,
  ...rest
}: SelectDropdownProps) => {
  const currentValue = defaultValue ?? value;
  const customSelectRef = createRef<HTMLDivElement>();
  const [showCustomSelectOptions, setShowCustomSelectOptions] = useState(false);
  const [hasValue, setHasValue] = useState(
    options.some((option) => option.value === value || option.value === defaultValue),
  );

  // Find selected option index if pre selected
  const optionIndexSelected = options.findIndex(({ value }) => value === currentValue);
  const defaultValueMatches = optionIndexSelected > -1;
  const currentOptionIndex = defaultValueMatches ? optionIndexSelected : 0;
  // Track custom select current option index position
  const [optionIndex, setOptionIndex] = useState<number>(currentOptionIndex);

  /**
   * Click handler for custom select option
   */
  const onClickHandler = (event: MouseEvent<HTMLDivElement>) => {
    const value = event.currentTarget.getAttribute('data-value')?.trim();
    const optionIndex = parseInt(event.currentTarget.getAttribute('data-index') as string, 10);

    if (value?.[0] && isNumber(optionIndex)) {
      setHasValue(true);
      setValue?.(value);
      onClick?.(value);
      setOptionIndex(optionIndex);
    }

    setShowCustomSelectOptions(false);
  };

  useEffect(() => {
    if (!defaultValue) {
      setHasValue(Boolean(value));
    }
  }, [value, defaultValue]);

  const handleKeyDown = (event: KeyboardEvent) => {
    // Check if custom select is active
    if (document.activeElement !== customSelectRef.current) return true;

    const maxLength = options.length - 2;
    const newOption = options.find((_, index) => index === optionIndex);

    if (SELECT_KEYBOARD_KEYS.includes(event.code)) {
      event.preventDefault();
    }

    switch (event.code) {
      case KEY_SPACE:
        if (!showCustomSelectOptions) {
          setShowCustomSelectOptions(true);
        } else if (newOption) {
          if (setValue) {
            setValue?.(newOption.value);
          }

          if (onClick) {
            onClick(newOption.value);
          }

          setShowCustomSelectOptions(false);
        }

        break;

      case KEY_ENTER:
        if (newOption) {
          if (setValue) {
            setValue?.(newOption.value);
          }

          if (onClick) {
            onClick(newOption.value);
          }

          setShowCustomSelectOptions(false);
        }

        break;

      case KEY_ARROW_DOWN:
        if (optionIndex <= maxLength) setOptionIndex(optionIndex + 1);

        break;
      case KEY_ARROW_UP:
        if (optionIndex > 0) setOptionIndex(optionIndex - 1);

        break;
      case KEY_TAB:
        if (showCustomSelectOptions) {
          setShowCustomSelectOptions(false);
        }

        break;
      case KEY_ESCAPE:
        setShowCustomSelectOptions(false);

        break;
      default:
        break;
    }
  };

  const hasPlaceholder = !!placeholder;
  const hasLabel = !!label;
  const ariaLabelledby = label ?? `label-${id}`;

  return (
    <>
      <WrapperComponent
        as="div"
        id={id}
        ref={customSelectRef}
        hasError={!!error}
        hasLabel={hasLabel}
        hasValue={!!value}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={(e: unknown) => {
          if (onFocus) {
            onFocus(e as FocusEvent<HTMLSelectElement>);
          }
          customSelectRef?.current?.focus();
        }}
        hasPlaceholder={hasPlaceholder}
        onClick={() => {
          // set focus custom select
          customSelectRef?.current?.focus();

          if (!showCustomSelectOptions) {
            setShowCustomSelectOptions(true);
          }
        }}
        onBlur={(e: unknown) => {
          customSelectRef?.current?.blur();

          if (onBlur) {
            onBlur(e as FocusEvent<HTMLSelectElement>);
          }
        }}
        // Hide the custom select from AT (e.g. SR) using aria-hidden
        aria-expanded={showCustomSelectOptions}
        aria-controls={`listbox-${id}`}
        aria-labelledby={ariaLabelledby}
        data-value={value}
        role="listbox"
        disabled={!!disabled}
        data-testid={rest['data-testid']}
      >
        {hasValue && (
          <CustomValue placeholderOnly={isDefined(placeholder) && !isDefined(label)}>
            {
              options.find((option) => {
                if (value) {
                  return option.value === value;
                }

                return option.value === defaultValue;
              })?.label
            }
          </CustomValue>
        )}
      </WrapperComponent>
      <AnimatePresence initial={false}>
        {showCustomSelectOptions && (
          <CustomOptions
            onClose={() => setShowCustomSelectOptions(false)}
            maxHeightOptions={maxHeightOptions}
            optionIndex={optionIndex}
            label={ariaLabelledby}
            id={id}
          >
            {options.map(({ value: optionValue, label }, index) => {
              // This shows the current state for keyboard navigation
              const isCurrent = optionIndex === index;
              const isActive =
                value === optionValue ||
                // active can also mean that the defaultValue matches option and no option has been selected yet!
                (defaultValue === optionValue && !options.some((option) => option.value === value));

              return (
                <StyledOption
                  key={`${id}-${optionValue}`}
                  data-value={optionValue}
                  data-index={index}
                  py="normal1"
                  px="normal2"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={onClickHandler}
                  active={isCurrent}
                  role="option"
                >
                  {label}
                  {isActive && <CheckIcon height="24" width="24" />}
                </StyledOption>
              );
            })}
          </CustomOptions>
        )}
      </AnimatePresence>
    </>
  );
};

const CustomValue = styled.span<{ placeholderOnly: boolean }>`
  position: relative;
  ${formStylesCss.font};

  ${({ placeholderOnly }) =>
    placeholderOnly &&
    css`
      align-items: center;
      display: inline-flex;
      height: 100%;
    `}
`;

export default SelectDropdown;
